type ResponseRecord = Record<string, unknown>;

const isRecord = (value: unknown): value is ResponseRecord =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Safely extracts data from response, handling multiple shapes:
 * - { data: [...] }
 * - { data: { items: [...] } }
 * - { data1: [...] }
 * - { Data1: [...] }
 * - { result: [...] }
 * - Raw array [...]
 * - Raw object { ... }
 */
export function normalizeResponse<T = unknown>(
  response: unknown,
  fallbackKey = 'data'
): T[] | T | null {
  if (response === null || response === undefined) {
    return fallbackKey === 'data' ? [] : null;
  }

  if (Array.isArray(response)) {
    return response as T[];
  }

  if (isRecord(response) && response.data !== undefined) {
    if (Array.isArray(response.data)) {
      return response.data as T[];
    }

    if (isRecord(response.data)) {
      return (
        response.data.items ??
        response.data.list ??
        response.data.rows ??
        response.data.data ??
        response.data.results ??
        response.data
      ) as T;
    }

    return response.data as T;
  }

  const data1 = isRecord(response)
    ? response.data1 ?? response.Data1
    : undefined;

  if (data1 !== undefined) {
    return Array.isArray(data1)
      ? (data1 as T[])
      : ([data1] as T[]);
  }

  if (isRecord(response) && response.result !== undefined) {
    return Array.isArray(response.result)
      ? (response.result as T[])
      : ([response.result] as T[]);
  }

  return response as T;
}

export function normalizeRow<T extends ResponseRecord = ResponseRecord>(
  row: unknown = {},
  fieldMapping: Record<string, string> = {}
): T {
  if (!isRecord(row)) {
    return {} as T;
  }

  const normalized: ResponseRecord = { ...row };

  if (Object.keys(fieldMapping).length > 0) {
    Object.entries(fieldMapping).forEach(([standardField, rawField]) => {
      if (normalized[rawField] !== undefined) {
        normalized[standardField] = normalized[rawField];
      }
    });
  }

  const idFields = ['id', 'Id', 'ID', 'uuid', 'UUID', 'key', 'Key'];
  for (const field of idFields) {
    if (normalized[field] !== undefined) {
      normalized.id = normalized[field];
      break;
    }
  }

  const nameFields = ['name', 'Name', 'name_en', 'displayName', 'display_name'];
  for (const field of nameFields) {
    if (normalized[field] !== undefined) {
      normalized.name = normalized[field];
      break;
    }
  }

  return normalized as T;
}

export function normalizeListResponse<T extends ResponseRecord = ResponseRecord>(
  response: unknown,
  rowNormalizer: (row: unknown, fieldMapping?: Record<string, string>) => T = normalizeRow,
  fieldMapping: Record<string, string> = {}
): T[] {
  const data = normalizeResponse<T[]>(response);

  if (!Array.isArray(data)) {
    return [];
  }

  return data.map((row) => rowNormalizer(row, fieldMapping));
}

export function normalizePaginatedResponse<T = unknown>(response: unknown) {
  const data = normalizeResponse<T[]>(response);
  const record = isRecord(response) ? response : {};

  return {
    data: Array.isArray(data) ? data : [],
    total: Number(record.total ?? record.totalCount ?? (Array.isArray(data) ? data.length : 0)),
    page: Number(record.page ?? record.currentPage ?? 1),
    pageSize: Number(record.pageSize ?? record.page_size ?? record.per_page ?? 10),
  };
}

export function createResponseEnvelope<T>(
  success: boolean,
  data: T | null = null,
  message = '',
  meta: ResponseRecord | null = null
) {
  return {
    success,
    data,
    message,
    meta,
    timestamp: new Date().toISOString(),
  };
}

/**
 * The payload of a Spring `common.dto.ApiResponse<T>`:
 * `{ IsSuccess, StatusCode, Message, Data1, Data }`, where `ApiResponse.success` fills `Data1`.
 * A wrapper with `IsSuccess: false` throws its `Message`. A body that is not a wrapper (for
 * example the unwrapped `sale-order-update-many` answer) is returned as it is.
 */
export function unwrapApiResponse<T>(response: unknown): T {
  if (!isRecord(response)) {
    return response as T;
  }

  const ok = response.IsSuccess ?? response.isSuccess;
  const isWrapper = ok !== undefined || 'Data1' in response;
  if (!isWrapper) {
    return response as T;
  }
  if (ok === false) {
    throw new Error(String(response.Message ?? 'Request failed'));
  }

  return (response.Data1 ?? response.Data ?? null) as T;
}

export default {
  normalizeResponse,
  normalizeRow,
  normalizeListResponse,
  normalizePaginatedResponse,
  createResponseEnvelope,
};
